import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import { Box, useTheme } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";

const BOTTOM_THRESHOLD = 4;

export interface ScrollbarProps {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  showHint?: boolean;
  hintScrollToBottomOnClick?: boolean;
  // Access to the scrolling element for callers that manage scroll
  // position themselves (e.g. chat threads following the tail).
  scrollRef?: React.Ref<HTMLDivElement>;
  onScroll?: React.UIEventHandler<HTMLDivElement>;
}

const Scrollbar: React.FC<ScrollbarProps> = ({
  children,
  sx = {},
  showHint = true,
  hintScrollToBottomOnClick = true,
  scrollRef: externalScrollRef,
  onScroll,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const setScrollRefs = useCallback(
    (node: HTMLDivElement | null) => {
      (scrollRef as React.MutableRefObject<HTMLDivElement | null>).current =
        node;
      if (typeof externalScrollRef === "function") {
        externalScrollRef(node);
      } else if (externalScrollRef) {
        (
          externalScrollRef as React.MutableRefObject<HTMLDivElement | null>
        ).current = node;
      }
    },
    [externalScrollRef],
  );

  useEffect(() => {
    const el = scrollRef.current;

    if (!el) {
      return;
    }

    const updateScrollState = () => {
      const hasOverflow = el.scrollHeight > el.clientHeight + BOTTOM_THRESHOLD;
      const atBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - BOTTOM_THRESHOLD;

      setIsAtBottom(!hasOverflow || atBottom);
    };

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);
    if (el.firstElementChild) {
      resizeObserver.observe(el.firstElementChild);
    }

    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
      resizeObserver.disconnect();
    };
  }, []);

  const scrollToBottom = useCallback(() => {
    if (!hintScrollToBottomOnClick) {
      return;
    }

    const el = scrollRef.current;
    if (!el) {
      return;
    }

    el.scrollTo({
      top: el.scrollHeight,
      behavior: "smooth",
    });
  }, [hintScrollToBottomOnClick]);

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        ...sx,
      }}
    >
      <Box
        ref={setScrollRefs}
        onScroll={onScroll}
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          flex: 1,
          overflowY: "scroll",
          overflowX: "hidden",
          scrollbarWidth: "thin",
          scrollbarColor: `${theme.palette.warning.main} transparent`,
          "&::-webkit-scrollbar": {
            width: 12,
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
            borderRadius: 999,
          },
          "&::-webkit-scrollbar-thumb": {
            background: `linear-gradient(180deg, ${theme.palette.warning.light}CC, ${theme.palette.warning.main}CC)`,
            borderRadius: 999,
            border: "3px solid transparent",
            minHeight: 48,
            boxShadow: `0 0 0 1px ${theme.palette.warning.dark}99`,
            animation: "scrollThumbPulse 1.8s ease-in-out infinite",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: `linear-gradient(180deg, ${theme.palette.warning.main}B3, ${theme.palette.warning.dark}B3)`,
          },
          "@keyframes scrollThumbPulse": {
            "0%, 100%": {
              filter: "brightness(1)",
            },
            "50%": {
              filter: "brightness(1.15)",
            },
          },
        }}
      >
        {children}
      </Box>

      {showHint && !isAtBottom ? (
        <Box
          role="button"
          tabIndex={0}
          aria-label={t("ui.scroll_bottom")}
          onClick={scrollToBottom}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              scrollToBottom();
            }
          }}
          sx={{
            position: "absolute",
            left: "50%",
            bottom: 12,
            transform: "translateX(-50%)",
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: hintScrollToBottomOnClick ? "pointer" : "default",
            pointerEvents: hintScrollToBottomOnClick ? "auto" : "none",
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: theme.palette.common.white,
            boxShadow: `0 0 0 1px ${theme.palette.warning.main}`,
            animation: "scrollHintBounce 1.2s ease-in-out infinite",
            opacity: 0.85,
            "@keyframes scrollHintBounce": {
              "0%, 100%": {
                transform: "translateX(-50%) translateY(0)",
              },
              "50%": {
                transform: "translateX(-50%) translateY(4px)",
              },
            },
          }}
        >
          <KeyboardDoubleArrowDownIcon
            sx={{ fontSize: 16, color: theme.palette.warning.main }}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default Scrollbar;
